import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, Printer, MapPin, Package, Settings } from 'lucide-react';
import { useReactToPrint } from 'react-to-print';
import { QRCodeCanvas } from 'qrcode.react';
import axios from 'axios';

function PrintDeliveries({ userId, setActiveComponent }) {
  const [selectedDeliveryPoints, setSelectedDeliveryPoints] = useState([]);
  const [deliveryPoints, setDeliveryPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [printerType, setPrinterType] = useState('a4');
  const printRef = useRef();

  useEffect(() => {
    fetchDeliveryPoints();
  }, [userId]);

  async function fetchDeliveryPoints() {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/user/${userId}`);
      setDeliveryPoints(response.data.delivery_points || []);
    } catch (error) {
      console.error('Error fetching delivery points:', error);
    } finally {
      setLoading(false);
    }
  }

  function handleSelectionChange(dp) {
    setSelectedDeliveryPoints(prev => {
      if (prev.some(item => item.id === dp.id)) {
        return prev.filter(item => item.id !== dp.id);
      } else {
        return [...prev, dp];
      }
    });
  }

  function handleSelectAll() {
    if (selectedDeliveryPoints.length === deliveryPoints.length) {
      setSelectedDeliveryPoints([]);
    } else {
      setSelectedDeliveryPoints([...deliveryPoints]);
    }
  }

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    pageStyle: printerType === 'thermal' 
      ? `
        @page {
          size: 80mm auto;
          margin: 0;
        }
        @media print {
          body { margin: 0; }
        }
      `
      : `
        @page {
          size: A4 portrait;
          margin: 10mm;
        }
        @media print {
          body { margin: 0; }
        }
      `
  });

  const allSelected = deliveryPoints.length > 0 && selectedDeliveryPoints.length === deliveryPoints.length;

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Fixed Header */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setActiveComponent('Analytics')}
              className="flex items-center gap-2 text-slate-700 hover:text-[#B1336E] transition-colors duration-200 font-montserrat"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm sm:text-base">Volver</span>
            </button>
            <h1 className="text-lg sm:text-xl font-extrabold text-[#B1336E] font-montserrat">
              Rotulado
            </h1>
            <div className="w-20"></div>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          
          {/* Instructions Card */}
          <div className="bg-gradient-to-r from-[#B1336E]/10 to-[#E6B43C]/10 border-l-4 border-[#B1336E] rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <Package className="w-5 h-5 text-[#B1336E] flex-shrink-0 mt-0.5" />
              <div>
                <h2 className="text-base font-bold text-[#B1336E] font-montserrat mb-1">
                  Selecciona las entregas a imprimir
                </h2>
                <p className="text-sm text-slate-700 font-montserrat">
                  Escoge las entregas que deseas imprimir en formato de etiquetas
                </p>
              </div>
            </div>
          </div>

          {/* Printer Type Selection */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
            <div className="flex items-center gap-3 mb-3">
              <Settings className="w-5 h-5 text-[#B1336E]" />
              <h3 className="text-base font-bold text-slate-900 font-montserrat">
                Tipo de Impresora
              </h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                onClick={() => setPrinterType('a4')}
                className={`p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                  printerType === 'a4'
                    ? 'border-[#B1336E] bg-[#B1336E]/5'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    printerType === 'a4' ? 'border-[#B1336E]' : 'border-slate-300'
                  }`}>
                    {printerType === 'a4' && (
                      <div className="w-3 h-3 rounded-full bg-[#B1336E]"></div>
                    )}
                  </div>
                  <span className="font-bold text-slate-900 font-montserrat">
                    Impresora A4
                  </span>
                </div>
                <p className="text-xs text-slate-600 font-montserrat ml-7">
                  Papel estándar A4 (210 × 297 mm)
                  <br />
                  8 etiquetas por hoja
                </p>
              </button>

              <button
                onClick={() => setPrinterType('thermal')}
                className={`p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                  printerType === 'thermal'
                    ? 'border-[#B1336E] bg-[#B1336E]/5'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    printerType === 'thermal' ? 'border-[#B1336E]' : 'border-slate-300'
                  }`}>
                    {printerType === 'thermal' && (
                      <div className="w-3 h-3 rounded-full bg-[#B1336E]"></div>
                    )}
                  </div>
                  <span className="font-bold text-slate-900 font-montserrat">
                    Impresora Térmica
                  </span>
                </div>
                <p className="text-xs text-slate-600 font-montserrat ml-7">
                  Xprinter XP-365B (80mm ancho)
                  <br />
                  1 etiqueta por rollo
                </p>
              </button>
            </div>
          </div>

          {/* Select All Button */}
          {deliveryPoints.length > 0 && (
            <div className="mb-4">
              <button
                onClick={handleSelectAll}
                className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-slate-50 border border-slate-300 rounded-lg transition-colors duration-200 font-montserrat text-sm"
              >
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors duration-200 ${
                  allSelected ? 'bg-[#B1336E] border-[#B1336E]' : 'border-slate-300'
                }`}>
                  {allSelected && (
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <span className="text-slate-700 font-medium">
                  {allSelected ? 'Deseleccionar todas' : 'Seleccionar todas'}
                </span>
              </button>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="w-12 h-12 border-4 border-[#B1336E] border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}

          {/* Empty State */}
          {!loading && deliveryPoints.length === 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
              <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-slate-900 font-montserrat mb-2">
                No hay entregas disponibles
              </h3>
              <p className="text-sm text-slate-600 font-montserrat">
                Crea una entrega para poder imprimir etiquetas
              </p>
            </div>
          )}

          {/* Delivery Points List */}
          {!loading && deliveryPoints.length > 0 && (
            <div className="space-y-3">
              {deliveryPoints.map(dp => {
                const isSelected = selectedDeliveryPoints.some(item => item.id === dp.id);
                return (
                  <button
                    key={dp.id}
                    onClick={() => handleSelectionChange(dp)}
                    className={`w-full bg-white rounded-xl border-2 transition-all duration-200 p-4 text-left ${
                      isSelected 
                        ? 'border-[#B1336E] shadow-md' 
                        : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors duration-200 ${
                        isSelected ? 'bg-[#B1336E] border-[#B1336E]' : 'border-slate-300'
                      }`}>
                        {isSelected && (
                          <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>

                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        isSelected ? 'bg-[#B1336E]' : 'bg-slate-100'
                      }`}>
                        <MapPin className={`w-6 h-6 ${isSelected ? 'text-white' : 'text-slate-500'}`} />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <h3 className="text-base font-bold text-slate-900 font-montserrat truncate">
                            {dp.recipient_name}
                          </h3>
                          <span className="text-xs font-semibold text-slate-500 font-montserrat whitespace-nowrap">
                            ID: {dp.id}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600 font-montserrat line-clamp-1 mb-1">
                          {dp.address}
                        </p>
                        {dp.distric && (
                          <p className="text-xs text-slate-500 font-montserrat">
                            {dp.distric}
                          </p>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

        </div>
      </div>

      {/* Fixed Footer - Print Button */}
      <div className="flex-shrink-0 bg-white border-t border-slate-200 shadow-lg">
        <div className="max-w-4xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <button
            onClick={handlePrint}
            disabled={selectedDeliveryPoints.length === 0}
            className="w-full bg-[#B1336E] hover:bg-[#B1336E]/90 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed font-montserrat flex items-center justify-center gap-2"
          >
            <Printer className="w-5 h-5" />
            <span>
              Imprimir {selectedDeliveryPoints.length > 0 && `(${selectedDeliveryPoints.length})`}
            </span>
          </button>
        </div>
      </div>

      {/* Hidden Print Content */}
      <div style={{ display: 'none' }}>
        <div ref={printRef}>
          {printerType === 'a4' ? (
            // A4 Format: 8 labels per page (2 columns × 4 rows)
            <>
              {(() => {
                const pages = [];
                for (let i = 0; i < selectedDeliveryPoints.length; i += 8) {
                  const pageDeliveries = selectedDeliveryPoints.slice(i, i + 8);
                  pages.push(
                    <div
                      key={`page-${i}`}
                      style={{
                        width: '210mm',
                        height: '297mm',
                        pageBreakAfter: i + 8 < selectedDeliveryPoints.length ? 'always' : 'auto',
                        fontFamily: 'Montserrat, sans-serif',
                        padding: '8mm',
                        boxSizing: 'border-box',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gridTemplateRows: 'repeat(4, 1fr)',
                        gap: '6mm',
                        width: '100%',
                        height: '100%'
                      }}>
                        {pageDeliveries.map((dp) => (
                          <div
                            key={dp.id}
                            style={{
                              border: '2px solid #B1336E',
                              padding: '4mm',
                              boxSizing: 'border-box',
                              position: 'relative',
                              backgroundColor: 'white',
                              borderRadius: '3mm',
                              display: 'flex',
                              flexDirection: 'column'
                            }}
                          >
                            <p style={{ 
                              color: '#B1336E', 
                              fontSize: '18px', 
                              fontWeight: 'bold',
                              margin: '0 0 2mm 0'
                            }}>
                              ID: {dp.id}
                            </p>
                            <p style={{ 
                              color: '#1e293b', 
                              fontSize: '13px',
                              fontWeight: '600',
                              margin: '0 0 1.5mm 0',
                              lineHeight: '1.2'
                            }}>
                              {dp.recipient_name}
                            </p>
                            {dp.product && (
                              <p style={{ 
                                color: '#475569', 
                                fontSize: '10px',
                                margin: '0 0 1.5mm 0',
                                lineHeight: '1.2'
                              }}>
                                {dp.product}
                              </p>
                            )}
                            {dp.distric && (
                              <p style={{ 
                                color: '#475569', 
                                fontSize: '10px',
                                fontWeight: '500',
                                margin: '0 0 1.5mm 0',
                                lineHeight: '1.2'
                              }}>
                                {dp.distric}
                              </p>
                            )}
                            <p style={{ 
                              color: '#475569', 
                              fontSize: '9px',
                              margin: '0',
                              paddingRight: '28mm',
                              lineHeight: '1.3'
                            }}>
                              {dp.address}
                            </p>
                            <div style={{
                              position: 'absolute',
                              bottom: '4mm',
                              right: '4mm',
                              padding: '1.5mm',
                              backgroundColor: 'white',
                              borderRadius: '2mm'
                            }}>
                              <QRCodeCanvas
                                value={dp.tracking_code}
                                size={75}
                                level="H"
                                includeMargin={false}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
                return pages;
              })()}
            </>
          ) : (
            // Thermal Format: 80mm width, continuous roll
            <div style={{ fontFamily: 'Montserrat, sans-serif' }}>
              {selectedDeliveryPoints.map((dp, index) => (
                <div
                  key={dp.id}
                  style={{
                    width: '80mm',
                    minHeight: '60mm',
                    border: '2px solid #B1336E',
                    padding: '4mm',
                    boxSizing: 'border-box',
                    backgroundColor: 'white',
                    marginBottom: '5mm',
                    pageBreakAfter: index < selectedDeliveryPoints.length - 1 ? 'always' : 'auto',
                    pageBreakInside: 'avoid',
                    borderRadius: '3mm'
                  }}
                >
                  <div style={{ textAlign: 'center', marginBottom: '3mm' }}>
                    <QRCodeCanvas
                      value={dp.tracking_code}
                      size={80}
                      level="H"
                      includeMargin={false}
                    />
                  </div>
                  <p style={{ 
                    color: '#B1336E', 
                    fontSize: '18px', 
                    fontWeight: 'bold',
                    margin: '0 0 2mm 0',
                    textAlign: 'center'
                  }}>
                    ID: {dp.id}
                  </p>
                  <p style={{ 
                    color: '#1e293b', 
                    fontSize: '13px',
                    fontWeight: '600',
                    margin: '0 0 2mm 0'
                  }}>
                    {dp.recipient_name}
                  </p>
                  {dp.product && (
                    <p style={{ 
                      color: '#475569', 
                      fontSize: '10px',
                      margin: '0 0 1mm 0'
                    }}>
                      {dp.product}
                    </p>
                  )}
                  {dp.distric && (
                    <p style={{ 
                      color: '#475569', 
                      fontSize: '10px',
                      fontWeight: '500',
                      margin: '0 0 1mm 0'
                    }}>
                      {dp.distric}
                    </p>
                  )}
                  <p style={{ 
                    color: '#475569', 
                    fontSize: '9px',
                    margin: '0'
                  }}>
                    {dp.address}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PrintDeliveries;